"use client"
// @deprecated The embedded frontend webphone is disabled. The Chrome extension now provides the webphone sidebar.
// import { useAppStoreContext } from "@/stores/appStore"
import { HeaderNavBar } from "@/components/generic/HeaderNavBar/HeaderNavBar"
import { Box } from "@mantine/core"

import { useMemo, type ReactNode } from "react"
import type { TLoginUserData } from "@/schemas/user.schema"
import { syncObservable } from "@legendapp/state/sync"
import { dynamoStore$ } from "@/stores/dynamo.v2.store"
import { observablePersistIndexedDB } from "@legendapp/state/persist-plugins/indexeddb"
import { useObserve } from "@legendapp/state/react"
import { syncStore$ } from "@/stores/sync.v2.store"
import classes from "./default.client.layout.module.css"
import clsx from "clsx"
// @deprecated The embedded frontend webphone is disabled. The Chrome extension now provides the webphone sidebar.
// import { WebphonePlaceholder } from "@/components/generic/WebphonePlaceholder/WebphonePlaceholder"
import { chatStore$ } from "@/lib/ContactsMap"
import { usePathname } from "next/navigation"
import { ChromeExtensionSidePanelToggle } from "@/components/webphone/ChromeExtensionSidePanelToggle"
import { SideNavBar } from "@/components/generic/SideNavBar/SideNavBar"

const FULLSCREEN_PATHS = ["/login", "/recover-password", "/sequences", "/sign-up", "/subscribe", "/subscribe/team", "/legacy-system"]

export function ChildrenLayout({
  children,
  classNames,
  showSequenceFeature,
  userData,
}: {
  children: ReactNode
  classNames?: {
    root?: string
  }
  showSequenceFeature?: boolean | null
  userData?: TLoginUserData
}) {
  const pathname = usePathname()
  // @deprecated The embedded frontend webphone is disabled. The Chrome extension now provides the webphone sidebar.
  // const { webphoneExpanded, webphoneDetached } = useAppStoreContext((s) => s)
  // const effectivelyExpanded = webphoneExpanded && !webphoneDetached

  const FULLSCREEN_EXACT_EXCLUDE_PATHS = useMemo(() => {
    return ["/sequences/library"]
  }, [])

  const isFullscreen = useMemo(() => {
    let _isFullscreen = false

    if (FULLSCREEN_EXACT_EXCLUDE_PATHS.includes(pathname)) {
      return false
    }

    for (const path of FULLSCREEN_PATHS) {
      if (pathname.startsWith(path)) {
        _isFullscreen = true
        break
      }
    }

    return _isFullscreen
  }, [FULLSCREEN_EXACT_EXCLUDE_PATHS, pathname])

  const containerClasses = useMemo(() => {
    if (isFullscreen) return classes.fullscreen
    return clsx(classes.noFullScreen, classes.noFullScreenWithoutWebphone)
  }, [isFullscreen])

  useObserve(() => {
    const accountID = syncStore$.userStatus.accountId.get()
    if (!accountID) return

    syncObservable(dynamoStore$, {
      persist: {
        name: "dynamoStore_v3", // This is the IndexedDB table name where we will store this "store"
        plugin: observablePersistIndexedDB({
          version: 3,
          databaseName: `Ringio-${encodeURIComponent(process.env.NEXT_PUBLIC_FRONT_URL ?? "unknown")}-${accountID}`,
          tableNames: ["chatStore_v3", "dynamoStore_v3"],
        }),
        transform: {
          load: (data) => {
            delete data?.liveUserContactInteractions

            delete data?._recentUserInteractionLastKey
            delete data?.isFetchingRecentUserInteractions

            delete data?._recentAccountInteractionLastKey
            delete data?.isFetchingRecentAccountInteractions

            delete data?._recentUserContactsLastKey
            delete data?.isFetchingRecentUserContacts

            delete data?._recentAccountContactsLastKey
            delete data?.isFetchingRecentAccountContacts

            delete data?._recentContactInteractionsLastKey
            delete data?.isFetchingRecentContactInteractions

            return data
          },
          save: (data) => {
            // In development, even if we don't use it, I want to save them, to visualize them
            if (process.env.NODE_ENV !== "development") {
              delete data?.liveUserContactInteractions

              delete data?._recentUserInteractionLastKey
              delete data?.isFetchingRecentUserInteractions

              delete data?._recentAccountInteractionLastKey
              delete data?.isFetchingRecentAccountInteractions

              delete data?._recentUserContactsLastKey
              delete data?.isFetchingRecentUserContacts

              delete data?._recentAccountContactsLastKey
              delete data?.isFetchingRecentAccountContacts

              delete data?._recentContactInteractionsLastKey
              delete data?.isFetchingRecentContactInteractions
            }
            return data
          },
        },
      },
    })

    syncObservable(chatStore$, {
      persist: {
        name: "chatStore_v4", // This is the IndexedDB table name where we will store this "store"
        // We use indexedDB so we don't have to worry about the size of the data we store
        plugin: observablePersistIndexedDB({
          version: 4,
          databaseName: `Ringio-${encodeURIComponent(process.env.NEXT_PUBLIC_FRONT_URL ?? "unknown")}-${accountID}`,
          tableNames: ["chatStore_v4", "dynamoStore_v4"],
        }),
        transform: {
          load: (data) => {
            // I think we don't need to save this
            // delete data?.interactions
            //
            delete data?.unreadChats
            delete data?.chats
            delete data?.sortedInteractions
            delete data?.searchChats
            delete data?.liveCalls
            delete data?._internal?.chatsMap
            delete data?._internal?.searchChatsMap
            delete data?._internal?.liveCallsMap
            delete data?._internal?.sortedInteractionsMap
            delete data?.chatLastInteraction

            return data
          },
          save: (data) => {
            // In development, even if we don't use it, I want to save them, to visualize them
            if (process.env.NODE_ENV !== "development") {
              // I think we don't need to save this
              // delete data?.interactions
              //
              delete data?.unreadChats
              delete data?.chats
              delete data?.sortedInteractions
              delete data?.searchChats
              delete data?.liveCalls
              delete data?._internal?.chatsMap
              delete data?._internal?.searchChatsMap
              delete data?._internal?.liveCallsMap
              delete data?._internal?.sortedInteractionsMap
              delete data?.chatLastInteraction
            }
            return data
          },
        },
      },
    })
  })

  return (
    <>
      {isFullscreen ? null : (
        <Box className={classes.headerNavBarContainer}>
          <HeaderNavBar userData={userData} />
        </Box>
      )}
      <Box className={clsx(containerClasses, classNames?.root)}>
        {isFullscreen ? null : (
          <Box className={classes.sideNavBarContainer}>
            <SideNavBar showSequenceFeature={showSequenceFeature} userData={userData} />
          </Box>
        )}
        <Box className={clsx(classes.appContainer, isFullscreen ? classes.isFullscreenAppContainer : classes.isNotFullscreenAppContainer)}>
          <Box className={classes.childrenContainer}>{children}</Box>
        </Box>
        {isFullscreen ? null : <ChromeExtensionSidePanelToggle />}
        {/* @deprecated The embedded frontend webphone is disabled. The Chrome extension now owns the webphone sidebar. */}
        {/* <WebphonePlaceholder webphoneCollapsed={!effectivelyExpanded} /> */}
      </Box>
    </>
  )
}
